import React, { useCallback, useState } from 'react';
import {
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	OutlinedInput,
	InputLabel,
	FormControl,
	InputAdornment,
	IconButton,
	FormHelperText,
	Input,
	Chip,
	Select,
	MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { UserDto, TokensDto } from '@oh-auth/dto';
import { useRecoilValue } from 'recoil';
import { apiClientState } from '../../features/api';
import { tokensState } from '../../features/auth';
import Lock from '@material-ui/icons/Lock';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import AccountCircle from '@material-ui/icons/AccountCircle';
import Star from '@material-ui/icons/Star';

interface UserFormProps {
	onClose: () => any;
	user: UserDto | null;
	onUserUpdate?: (user: UserDto) => void;
	onUserCreate?: (user: UserDto) => void;
}

interface FormState {
	username: string;
	nick_name: string;
	password: string;
	groups: string[];
}

const useStyles = makeStyles((theme) => ({
	textField: {
		margin: theme.spacing(1),
		width: 400,
		height: 56,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: 2,
	},
}));

const userGroups = [
	'root',
	'jwe',
	'poly',
	'tmo',
	'lcsd_viewer',
	'lcsd_editor',
	'hyd_viewer',
	'hyd_editor',
	'archsd_viewer',
	'archsd_editor',
	'hd_viewer',
	'hd_editor',
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

export function UserForm(props: UserFormProps) {
	const { user, onClose, onUserUpdate, onUserCreate } = props;
	const [showPassword, setShowPassword] = useState(false);

	const classes = useStyles();

	const { t } = useTranslation();
	const tokens = useRecoilValue(tokensState) as TokensDto;
	const apiClient = useRecoilValue(apiClientState);

	const toggleVisibility = () => setShowPassword((s) => !s);

	const handleSubmit = useCallback(
		(update: FormState) => {
			const { username, groups, nick_name, password } = update;
			if (user) {
				apiClient
					.updateUser(tokens, {
						user_id: user.user_id,
						groups,
						nick_name,
						...(password.length >= 8 ? { password } : {}),
					})
					.then((user) => {
						onUserUpdate?.(user);
						onClose();
					});
			} else {
				apiClient
					.createUser(tokens, {
						username,
						password,
						groups,
						nick_name,
					})
					.then((user) => {
						onUserCreate?.(user);
						onClose();
					});
			}
		},
		[apiClient, tokens, user, onUserUpdate, onUserCreate, onClose]
	);

	const handleValidate = useCallback(
		(values: FormState) => {
			const errors: any = {};
			if (values.password.length > 0 && values.password.length < 8) {
				errors.password = t('userCard.passwordTooShort');
			}
			return errors;
		},
		[t]
	);

	const { values, handleChange, errors, submitForm } = useFormik<FormState>({
		initialValues: {
			username: '',
			nick_name: user?.nick_name || '',
			password: '',
			groups: user?.groups || [],
		},
		onSubmit: handleSubmit,
		validate: handleValidate,
		validateOnChange: true,
	});

	return (
		<>
			<DialogTitle>
				{t(user ? 'userCard.updateUser' : 'userCard.createUser')}
			</DialogTitle>
			<DialogContent>
				{!user ? (
					<FormControl
						variant="outlined"
						className={classes.textField}
					>
						<InputLabel htmlFor={'newuser-nickname-field'}>
							{t('userCard.username')}
						</InputLabel>
						<OutlinedInput
							id={'newuser-nickname-field'}
							value={values.username}
							onChange={handleChange('username')}
							error={!!errors.username}
							startAdornment={
								<InputAdornment position="start">
									<AccountCircle />
								</InputAdornment>
							}
							labelWidth={70}
						/>
					</FormControl>
				) : null}
				<FormControl variant="outlined" className={classes.textField}>
					<InputLabel
						htmlFor={`${user?.user_id || 'newuser'}-nickname-field`}
					>
						{t('userCard.nickname')}
					</InputLabel>
					<OutlinedInput
						id={`${user?.user_id || 'newuser'}-nickname-field`}
						value={values.nick_name}
						onChange={handleChange('nick_name')}
						error={!!errors.nick_name}
						startAdornment={
							<InputAdornment position="start">
								<Star />
							</InputAdornment>
						}
						labelWidth={70}
					/>
					{errors.nick_name ? (
						<FormHelperText>{errors.nick_name}</FormHelperText>
					) : null}
				</FormControl>
				<FormControl variant="outlined" className={classes.textField}>
					<InputLabel
						htmlFor={`${user?.user_id || 'newuser'}-password-field`}
					>
						{t('userCard.password')}
					</InputLabel>
					<OutlinedInput
						id={`${user?.user_id || 'newuser'}-password-field`}
						type={showPassword ? 'text' : 'password'}
						value={values.password}
						onChange={handleChange('password')}
						error={!!errors.password}
						startAdornment={
							<InputAdornment position="start">
								<Lock />
							</InputAdornment>
						}
						endAdornment={
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={toggleVisibility}
									onMouseDown={toggleVisibility}
									edge="end"
								>
									{showPassword ? (
										<Visibility />
									) : (
										<VisibilityOff />
									)}
								</IconButton>
							</InputAdornment>
						}
						labelWidth={70}
					/>
					{errors.password ? (
						<FormHelperText>{errors.password}</FormHelperText>
					) : null}
				</FormControl>
				<FormControl className={classes.textField}>
					<InputLabel
						id={`${user?.user_id || 'newuser'}-groups-select-label`}
					>
						{t('userCard.groups')}
					</InputLabel>
					<Select
						labelId={`${
							user?.user_id || 'newuser'
						}-groups-select-label`}
						id={`${user?.user_id || 'newuser'}-groups-select`}
						multiple
						value={values.groups}
						onChange={handleChange('groups')}
						input={
							<Input
								id={`${
									user?.user_id || 'newuser'
								}-groups-select-input`}
							/>
						}
						renderValue={(selected: any) => (
							<div className={classes.chips}>
								{selected.map((value: string) => (
									<Chip
										key={value}
										label={value}
										className={classes.chip}
									/>
								))}
							</div>
						)}
						MenuProps={MenuProps}
					>
						{userGroups.map((group) => (
							<MenuItem key={group} value={group}>
								{group}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={submitForm}
					disabled={values.password !== '' && !!errors.password}
					color="primary"
				>
					{user ? t('userCard.update') : t('userCard.confirm')}
				</Button>
				<Button onClick={onClose} color="primary">
					{t('userCard.cancel')}
				</Button>
			</DialogActions>
		</>
	);
}
